import { forwardRef, useRef } from 'react';
import { Button, Dropdown, Popover, Whisper } from 'rsuite';
import { MonitoringItem } from '../types';
import styles from './ActionPopover.module.scss';

export enum ActionPopoverEventKey {
  OPEN_ANALYSIS = 'open-analysis',
  EDIT = 'edit',
  DUPLICATE = 'duplicate',
  TOGGLE_ACTIVE = 'toggle-active',
  DELETE = 'delete',
  REMOVE_FOR_ME = 'remove-for-me',
}

export type ActionPopoverProps = {
  item: MonitoringItem;
  onOpenAnalysis?: (item: MonitoringItem) => void;
  onEdit?: (item: MonitoringItem) => void;
  onDuplicate?: (item: MonitoringItem) => void;
  onToggleActive?: (item: MonitoringItem, ativo: boolean) => void;
  onDelete?: (item: MonitoringItem) => void;
  onRemoveForMe?: (item: MonitoringItem) => void;
  triggerClassName?: string;
};

type MenuPopoverProps = {
  onSelect: (eventKey: ActionPopoverEventKey) => void;
  toggleActiveLabel: string;
  isCompartilhado: boolean;
};

// Item compartilhado: o usuário logado é só um destinatário, não o dono. Só pode abrir a análise ou
// remover o monitoramento da própria Central — edição, duplicação, ativação e exclusão são do dono.
const MenuPopover = forwardRef<HTMLDivElement, MenuPopoverProps>(({ onSelect, toggleActiveLabel, isCompartilhado, ...rest }, ref) => (
  <Popover ref={ref} {...rest} full className={styles.popover}>
    <Dropdown.Menu onSelect={onSelect} className={styles.menu}>
      <Dropdown.Item eventKey={ActionPopoverEventKey.OPEN_ANALYSIS} className={styles.menuItem}>
        Abrir análise
      </Dropdown.Item>
      {isCompartilhado
        ? [
            <Dropdown.Item key="remove-for-me" eventKey={ActionPopoverEventKey.REMOVE_FOR_ME} className={styles.menuItem}>
              Remover para mim
            </Dropdown.Item>,
          ]
        : [
            <Dropdown.Item key="edit" eventKey={ActionPopoverEventKey.EDIT} className={styles.menuItem}>
              Editar
            </Dropdown.Item>,
            <Dropdown.Item key="duplicate" eventKey={ActionPopoverEventKey.DUPLICATE} className={styles.menuItem}>
              Criar novo a partir desse
            </Dropdown.Item>,
            <Dropdown.Item key="toggle-active" eventKey={ActionPopoverEventKey.TOGGLE_ACTIVE} className={styles.menuItem}>
              {toggleActiveLabel}
            </Dropdown.Item>,
            <Dropdown.Item key="delete" eventKey={ActionPopoverEventKey.DELETE} className={styles.menuItem}>
              Excluir
            </Dropdown.Item>,
          ]}
    </Dropdown.Menu>
  </Popover>
));

MenuPopover.displayName = 'MenuPopover';

export const ActionPopover = ({ item, onOpenAnalysis, onEdit, onDuplicate, onToggleActive, onDelete, onRemoveForMe, triggerClassName }: ActionPopoverProps) => {
  const toggleActiveLabel = item.ativo ? 'Inativar monitoramento' : 'Ativar monitoramento';
  const whisperRef = useRef<any>(null);

  const handleSelect = (eventKey: ActionPopoverEventKey) => {
    whisperRef.current?.hide?.();

    switch (eventKey) {
      case ActionPopoverEventKey.OPEN_ANALYSIS:
        onOpenAnalysis?.(item);
        break;
      case ActionPopoverEventKey.EDIT:
        onEdit?.(item);
        break;
      case ActionPopoverEventKey.DUPLICATE:
        onDuplicate?.(item);
        break;
      case ActionPopoverEventKey.TOGGLE_ACTIVE:
        onToggleActive?.(item, !item.ativo);
        break;
      case ActionPopoverEventKey.DELETE:
        onDelete?.(item);
        break;
      case ActionPopoverEventKey.REMOVE_FOR_ME:
        onRemoveForMe?.(item);
        break;
      default:
        break;
    }
  };

  return (
    <Whisper
      triggerRef={whisperRef}
      placement="autoVerticalEnd"
      trigger="click"
      speaker={<MenuPopover onSelect={handleSelect} toggleActiveLabel={toggleActiveLabel} isCompartilhado={!!item.compartilhado} />}
    >
      <Button appearance="link" className={triggerClassName}>
        Mais...
      </Button>
    </Whisper>
  );
};
